import { ArrowRightIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { membersTransactions } from 'rails_routes'
import { ButtonLink, useBreakpoint } from 'slab'
import { getLocalizedDateString } from 'utils'
import { useGetMembersTransactions } from 'utils/web_api'
import { type BalanceTransaction } from 'utils/web_api.schemas'

import { SkeletonItem } from '../../../reusable/SkeletonItem'
import { getMoneyClasses, getMoneyText } from '../../utils'

type RecentTransactionsProps = {
  planYear: number;
  isPriorYear: boolean;
  nextToAnotherCard: boolean;
  data?: BalanceTransaction[];
  isLoading?: boolean;
  isError?: boolean;
}

const makeTransactionRow = (transaction: BalanceTransaction, renderAsRow: boolean) => {
  const localizedDate = getLocalizedDateString(new Date(transaction.date))

  const dateAndDescription = renderAsRow
    ? (
      <div className='flex gap-1'>
        <div className='body text-gray-500 w-40 shrink-0'>{localizedDate}</div>
        <div className='body text-main'>{transaction.description}</div>
      </div>
      )
    : (
      <div className='flex flex-col gap-1'>
        <div className='body text-main'>{transaction.description}</div>
        <div className='body-sm text-gray-500'>{localizedDate}</div>
      </div>
      )

  return (
    <div key={transaction.id} className='flex justify-between gap-4'>
      {dateAndDescription}
      <div className={getMoneyClasses(transaction.amount)}>{getMoneyText(transaction.amount)}</div>
    </div>
  )
}

const makeSkeletonRow = (skeletonNumber: number, renderAsRow: boolean) => {
  const bodyTextHeight = 'h-5'

  const skeleton = renderAsRow
    ? (
      <>
        <div className='flex gap-8'>
          <SkeletonItem heightClass={bodyTextHeight} widthClass='w-24' />
          <SkeletonItem heightClass={bodyTextHeight} widthClass={skeletonNumber % 2 === 0 ? 'w-60' : 'w-80'} />
        </div>
        <SkeletonItem heightClass={bodyTextHeight} widthClass={skeletonNumber % 2 === 0 ? 'w-28' : 'w-24'} />
      </>
      )
    : (
      <>
        <div className='flex flex-col gap-1 w-[75%]'>
          <SkeletonItem heightClass={bodyTextHeight} widthClass={skeletonNumber % 2 === 0 ? 'w-[70%]' : 'w-[60%]'} />
          <SkeletonItem heightClass='h-[1.125rem]' widthClass='w-24' />
        </div>
        <div className='flex-1 flex justify-end'>
          <SkeletonItem heightClass={bodyTextHeight} widthClass={skeletonNumber % 2 === 0 ? 'w-28' : 'w-24'} />
        </div>
      </>
      )

  return (
    <div key={`recent-transactions-skeleton-${skeletonNumber}`} className='flex justify-between gap-4'>
      {skeleton}
    </div>
  )
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  planYear,
  isPriorYear,
  nextToAnotherCard = false,
  data: providedData,
  isLoading: providedIsLoading = false,
  isError: providedIsError = false,
}) => {
  const { breakpointIsAtLeast } = useBreakpoint()

  const isAtLeastLg = breakpointIsAtLeast('lg')
  const renderAsRow = isAtLeastLg && !nextToAnotherCard

  const shouldFetch = providedData === undefined

  const { data: hookData, isLoading: hookIsLoading, isError: hookIsError } = useGetMembersTransactions({
    plan_year: planYear,
    'page[number]': 1,
    'page[size]': 3,
  }, {
    query: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: shouldFetch, // Only fetch if data prop is not provided
    }
  })

  // Use provided data if available, otherwise use hook data
  const actualData = shouldFetch ? hookData?.data : providedData
  const actualIsLoading = shouldFetch ? hookIsLoading : providedIsLoading
  const actualIsError = shouldFetch ? hookIsError : providedIsError

  const innerContent = useMemo(() => {
    const buttonContainerClasses = isAtLeastLg ? 'w-full flex justify-end border-t border-gray-200 pt-4 mt-4' : 'w-full flex justify-center pt-4'
    const buttonWidth = isAtLeastLg ? undefined : 'full'
    const skeletonButtonWidth = isAtLeastLg ? 'w-40' : 'w-full'

    const viewAllTransactionsButton = (
      <div className={buttonContainerClasses}>
        <ButtonLink
          href={membersTransactions.index({ query: { plan_year: planYear } })}
          rightIcon={ArrowRightIcon}
          variant='secondary'
          size='large'
          width={buttonWidth}
        >
          View all transactions
        </ButtonLink>
      </div>
    )

    if (actualIsError) {
      return (
        <div className='flex flex-col items-center justify-between min-h-48'>
          <div className='flex-1 flex items-center justify-center'>
            <p className='body text-gray-500 text-center'>We're having trouble loading your recent transactions</p>
          </div>
          {viewAllTransactionsButton}
        </div>
      )
    }

    if (actualIsLoading) {
      return (
        <>
          <div className='flex flex-col gap-4'>
            {[1, 2, 3].map((i) => (
              makeSkeletonRow(i, renderAsRow)
            ))}
          </div>
          <div className={buttonContainerClasses}>
            <div className={`w-full flex ${isAtLeastLg ? 'justify-end' : 'justify-center'}`}>
              <SkeletonItem heightClass='h-10' widthClass={skeletonButtonWidth} />
            </div>
          </div>
        </>
      )
    }

    if (!actualData || actualData.length === 0) {
      return (
        <div className='flex flex-col items-center justify-center min-h-48'>
          <div className='text-center space-y-4'>
            <p className='body-bold text-gray-500'>No transactions yet</p>
          </div>
        </div>
      )
    }

    return (
      <>
        <div className='flex flex-col gap-4'>
          {actualData.map((transaction) => (
            makeTransactionRow(transaction, renderAsRow)
          ))}
        </div>
        {viewAllTransactionsButton}
      </>
    )
  }, [isAtLeastLg, renderAsRow, actualIsError, actualIsLoading, actualData])

  const bgColor = isPriorYear ? 'bg-gray-50' : 'bg-white'

  return (
    <div className={`w-full h-full flex flex-col p-4 rounded-2xl border border-gray-200 ${bgColor} shadow-sm`}>
      {innerContent}
    </div>
  )
}

export default RecentTransactions
